import React from "react";
import ProductCard from "../components/ProductCard";
import products from "../data/flowerVendors";
import { useLocation } from "react-router-dom";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function Listings() {
  const query = useQuery();
  const q = query.get("q") || "";
  const loc = (query.get("loc") || "").toLowerCase();

  // Filter products by search term and simple location rule (Adyar => local vendors)
  const filtered = products.filter((p) => {
    const matchesQuery = q ? p.name.toLowerCase().includes(q.toLowerCase()) || (p.description || "").toLowerCase().includes(q.toLowerCase()) : true;
    if (!matchesQuery) return false;

    if (loc.includes("adyar")) {
      // treat 'Adyar' as local: distance <= 5 km
      return typeof p.distance_km === "number" ? p.distance_km <= 5 : true;
    }

    // for other locations, no distance filtering for now
    return true;
  });

  return (
    <main className="max-w-[1280px] mx-auto px-6 py-8">
      <div className="mb-8">
        <nav className="flex items-center gap-2 text-xs font-medium text-[#9a8d4c] mb-4">
          <a className="hover:text-primary transition-colors" href="#">
            Home
          </a>
          <span className="material-symbols-outlined !text-xs">chevron_right</span>
          <a className="hover:text-primary transition-colors" href="#">
            Wedding Services
          </a>
          <span className="material-symbols-outlined !text-xs">chevron_right</span>
          <span className="text-[#1b190d] dark:text-white">Floral Artisans</span>
        </nav>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h2 className="text-3xl font-black tracking-tight mb-2">Floral Artisans &amp; Garland Makers</h2>
            <p className="text-[#9a8d4c]">Found {filtered.length} verified artisans in your local area</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-[#9a8d4c]">Sort by:</span>
            <select className="bg-white dark:bg-background-dark border-[#e7e3cf] dark:border-[#3d3a2a] rounded-lg text-sm font-semibold focus:ring-primary focus:border-primary px-4 py-2 pr-10">
              <option>Recommended</option>
              <option>Highest Rating</option>
              <option>Price: Low to High</option>
              <option>Price: High to Low</option>
              <option>Distance: Nearest</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex gap-8">
        <aside className="w-72 shrink-0 hidden lg:block">
          <div className="sticky top-24 bg-white dark:bg-background-dark border border-[#e7e3cf] dark:border-[#3d3a2a] rounded-xl p-6 space-y-8">
            <div className="flex items-center justify-between">
              <h3 className="font-black text-lg">Filters</h3>
              <button className="text-xs font-bold text-primary hover:underline">
                Reset All
              </button>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#9a8d4c]">
                Service Type
              </h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    checked=""
                    className="rounded text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    type="checkbox"
                  />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">
                    Wedding Garland
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    className="rounded text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    type="checkbox"
                  />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">
                    Daily Puja Flowers
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    className="rounded text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    type="checkbox"
                  />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">
                    Event Decorations
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    className="rounded text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    type="checkbox"
                  />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">
                    Bridal Bouquet
                  </span>
                </label>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#9a8d4c]">
                Price Range
              </h4>
              <div className="px-2">
                <input
                  className="w-full accent-primary h-1.5 bg-[#f3f1e7] dark:bg-[#3d3a2a] rounded-lg appearance-none cursor-pointer"
                  type="range"
                />
                <div className="flex justify-between mt-3">
                  <span className="text-xs font-bold">$20</span>
                  <span className="text-xs font-bold">$500+</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#9a8d4c]">
                Minimum Rating
              </h4>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    className="text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    name="rating"
                    type="radio"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold">4.5 &amp; up</span>
                    <div className="flex text-primary">
                      <span className="material-symbols-outlined !text-sm">
                        star
                      </span>
                    </div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <input
                    className="text-primary focus:ring-primary border-[#e7e3cf] dark:border-[#3d3a2a] bg-transparent"
                    name="rating"
                    type="radio"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold">4.0 &amp; up</span>
                    <div className="flex text-primary">
                      <span className="material-symbols-outlined !text-sm">
                        star
                      </span>
                    </div>
                  </div>
                </label>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#9a8d4c]">
                Location Distance
              </h4>
              <div className="px-2">
                <input
                  className="w-full accent-primary h-1.5 bg-[#f3f1e7] dark:bg-[#3d3a2a] rounded-lg appearance-none cursor-pointer"
                  type="range"
                />
                <div className="flex justify-between mt-3">
                  <span className="text-xs font-bold">Local (5km)</span>
                  <span className="text-xs font-bold">Anywhere</span>
                </div>
              </div>
            </div>
            <button className="w-full py-3 bg-[#f3f1e7] dark:bg-[#3d3a2a] text-[#1b190d] dark:text-white rounded-lg font-bold text-sm hover:bg-primary hover:text-background-dark transition-all">
              Apply Filters
            </button>
          </div>
        </aside>
        <div className="flex-1">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <div key={p.id}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center items-center gap-2">
            <button className="w-10 h-10 rounded-lg border border-[#e7e3cf] dark:border-[#3d3a2a] flex items-center justify-center hover:bg-primary hover:text-background-dark transition-colors">
              <span className="material-symbols-outlined !text-base">
                chevron_left
              </span>
            </button>
            <button className="w-10 h-10 rounded-lg bg-primary text-background-dark font-black flex items-center justify-center shadow-md">
              1
            </button>
            <button className="w-10 h-10 rounded-lg border border-[#e7e3cf] dark:border-[#3d3a2a] flex items-center justify-center hover:bg-primary hover:text-background-dark transition-colors font-bold">
              2
            </button>
            <button className="w-10 h-10 rounded-lg border border-[#e7e3cf] dark:border-[#3d3a2a] flex items-center justify-center hover:bg-primary hover:text-background-dark transition-colors font-bold">
              3
            </button>
            <span className="mx-2 text-[#9a8d4c]">...</span>
            <button className="w-10 h-10 rounded-lg border border-[#e7e3cf] dark:border-[#3d3a2a] flex items-center justify-center hover:bg-primary hover:text-background-dark transition-colors font-bold">
              12
            </button>
            <button className="w-10 h-10 rounded-lg border border-[#e7e3cf] dark:border-[#3d3a2a] flex items-center justify-center hover:bg-primary hover:text-background-dark transition-colors">
              <span className="material-symbols-outlined !text-base">
                chevron_right
              </span>
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
